import React, { useContext } from "react"
import PropTypes from "prop-types"
import propTypes from "@styled-system/prop-types"
import { Link as GatsbyLink, graphql, notGatsby } from "gatsby"
import styled, { css } from "styled-components"
import { space, layout, compose, color } from "styled-system"
import { shouldForwardProp } from "../../lib/styled-system/helpers.js"

import { PopupsContext } from "../../contexts/PopupsContext"
import { useSitePages } from "../../hooks/useSitePagesData.js"
import { getPagePath } from "../../utils/page.js"
import useVisitedLinks from "../../hooks/useVisitedLinks.js"

// Apply margin and padding to links, active
const StyledGatsbyLink = GatsbyLink
  ? styled(GatsbyLink).withConfig({ shouldForwardProp })`
      ${compose(space, color, layout)};

      ${props =>
        css({
          "&.visited": {
            "& span": {
              ...props.visited, //adding CSS style to visited link container
            },
            "&:hover": {
              "& span": {
                ...props.hover, //adding CSS style to visited link container
              },
            },
          },
        })}
    `
  : null

const StyledAnchor = styled("a").withConfig({ shouldForwardProp })`
  ${compose(space, layout)};
  cursor: pointer;

  ${props =>
    css({
      "&:visited": {
        "& span": {
          ...props.visited, //adding CSS style to visited link container
        },
      },
      "&:hover": {
        "& span": {
          ...props.hover, //adding CSS style to visited link container
        },
      },
    })}
`

/**
 * Switches between a Gatsby link and a regular anchor tag based on props
 * @param {string | number} to - an internal pageId
 * @param {sectionId | string} sectionId - id of section to link to
 * @param {string} href - an external link
 * @param {string} linkText - Text for the link. Usually sent as "children".
 * @param {boolean} optional - don't include <a> tag if no link provided
 * @param {boolean} partiallyActive - Gatsby partiallyActive
 */
const Link = ({
  to,
  sectionId,
  href,
  linkText,
  optional = false,
  partiallyActive = false,
  children,
  linkOpenTarget,
  preserveURLParams = true,
  visited,
  className = "",
  onClick,
  hover,
  ...rest
}) => {
  const { handleVisit, isVisited } = useVisitedLinks()

  const { openModalSectionById } = useContext(PopupsContext)
  const sitePages = useSitePages()

  const linkId = to
    ? `${to}${sectionId ? `-${sectionId}` : ""}${
        linkText ? `-${linkText}` : ""
      }`
    : ""

  // Fallback
  if (notGatsby) {
    if (!to && !href && optional) {
      return <>{children}</>
    }
    return (
      <StyledAnchor
        className={className}
        visited={visited}
        hover={hover}
        {...rest}
      >
        {children}
      </StyledAnchor>
    )
  }

  const isLinkAPopup = sectionId?.includes("popups")

  const onClickHandler = () => {
    if (onClick) onClick()
    if (isLinkAPopup && openModalSectionById) {
      openModalSectionById(sectionId)
    }
    if (to && linkId && !isLinkAPopup) handleVisit(linkId) // Store the link ID as the visited state identifier
  }

  // Normal Gatsby component
  let queryParams =
    preserveURLParams && typeof window !== "undefined"
      ? window?.location?.search || ""
      : ""

  //For popups
  if (isLinkAPopup) {
    return (
      <StyledAnchor
        className={className}
        onClick={onClickHandler}
        visited={visited}
        hover={hover} // Check if the link ID is visited
        {...rest}
      >
        {children}
      </StyledAnchor>
    )
  }

  if (to) {
    let pagePath = getPagePath({
      to,
      sitePages,
      sectionId,
    })

    if (partiallyActive && pagePath === "/") {
      partiallyActive = false
    }

    if (pagePath) {
      const linkUrl = pagePath + queryParams // persisting url params while browsing the site

      return (
        <StyledGatsbyLink
          activeClassName={partiallyActive ? "active" : ""}
          className={
            isVisited(linkId) ? `${className} visited` : `${className}`
          } // Add the "visited" class if the link ID is visited
          onClick={onClickHandler}
          to={linkUrl}
          visited={visited}
          hover={hover}
          {...rest}
        >
          {children}
        </StyledGatsbyLink>
      )
    }
  }

  if (href) {
    return (
      <StyledAnchor
        className={className}
        href={href}
        rel="noopener"
        target={linkOpenTarget === "sameTab" ? "_self" : "_blank"}
        visited={visited}
        hover={hover}
        {...rest}
      >
        {children}
      </StyledAnchor>
    )
  }

  if (optional) {
    return <>{children}</>
  }

  return (
    <StyledAnchor
      className={className}
      visited={visited}
      hover={hover}
      {...rest}
    >
      {children}
    </StyledAnchor>
  )
}

Link.strapiProps = {
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Number corresponding to the page id or exact string path
  sectionId: PropTypes.string,
  href: PropTypes.string, // Url of external site
  linkText: PropTypes.string,
}

Link.propTypes = {
  ...Link.strapiProps,
  optional: PropTypes.bool,
  partiallyActive: PropTypes.bool,
  children: PropTypes.node.isRequired,
  ...propTypes.space,
}

export default Link

export const query = graphql`
  fragment Link on STRAPI__COMPONENT_UTILS_LINK {
    to
    sectionId
    href
    linkText
    linkOpenTarget
  }
`
